import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfilePage.module.css';
import { Header } from '../../components/layout/Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usersAPI, authAPI } from '../../services/api';

const roleLabels: Record<string, string> = {
  superAdmin: 'Super Admin',
  admin: 'Admin',
  projectManager: 'Projectmanager',
  stakeholder: 'Stakeholder',
  viewer: 'Lezer',
};

export const ProfilePage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    department: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      const names = user.name?.split(' ') || [];
      setProfileData({
        firstName: (user as any).firstName || names[0] || '',
        lastName: (user as any).lastName || names.slice(1).join(' ') || '',
        phone: (user as any).phone || '',
        department: (user as any).department || '',
      });
    }
  }, [user]);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    setSavingProfile(true);
    try {
      await usersAPI.update(user.id, profileData);
      await refreshUser();
      toast.success('Profiel bijgewerkt', 'Je gegevens zijn opgeslagen.');
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen', 'Controleer het bevestigingswachtwoord.');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Wachtwoord te kort', 'Het wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.updatePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Wachtwoord gewijzigd', 'Je wachtwoord is succesvol bijgewerkt.');
    } catch (error: any) {
      toast.error('Wijzigen mislukt', error.response?.data?.error || 'Controleer je huidige wachtwoord.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bestand te groot', 'Maximaal 5 MB toegestaan.');
      return;
    }

    setUploadingAvatar(true);
    try {
      await usersAPI.uploadAvatar(user.id, file);
      await refreshUser();
      toast.success('Profielfoto bijgewerkt');
    } catch (error: any) {
      toast.error('Upload mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!user) return null;

  const companyName = typeof (user as any).company === 'object'
    ? (user as any).company?.name
    : (user as any).company || '';

  return (
    <div className={styles.profilePage}>
      <Header
        user={user}
        onLogout={logout}
        showProjectSelector={false}
      />

      <div className={styles.profileBody}>
        <div className={styles.profileContent}>
          <a
            href="/projects"
            className={styles.backLink}
            onClick={(e) => { e.preventDefault(); navigate('/projects'); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Terug naar projecten
          </a>

          <h1 className={styles.pageTitle}>Profiel</h1>
          <p className={styles.pageSubtitle}>Beheer je persoonlijke gegevens</p>

          {/* Avatar Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Profielfoto</h2>
            <div className={styles.avatarSection}>
              <div className={styles.avatarPreview}>
                {user.avatar ? (
                  <img src={user.avatar} alt="" className={styles.avatarImage} />
                ) : (
                  <span className={styles.avatarInitials}>
                    {user.initials || user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className={styles.avatarActions}>
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {uploadingAvatar ? 'Uploaden...' : 'Foto uploaden'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
                <span className={styles.avatarHint}>JPG, PNG of WebP. Max 5 MB.</span>
              </div>
            </div>
          </div>

          {/* Account Info (read-only) */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Account</h2>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>E-mail</label>
                <div className={styles.readOnlyValue}>{user.email}</div>
              </div>
              <div className={styles.formGroup}>
                <label>Rol</label>
                <div className={styles.readOnlyValue}>
                  <span className={styles.roleBadge}>
                    {roleLabels[user.role || ''] || user.role || 'Onbekend'}
                  </span>
                </div>
              </div>
            </div>
            {companyName && (
              <div className={styles.formGroup}>
                <label>Organisatie</label>
                <div className={styles.readOnlyValue}>{companyName}</div>
              </div>
            )}
          </div>

          {/* Editable Profile */}
          <form onSubmit={handleProfileSave}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Persoonsgegevens</h2>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="firstName">Voornaam</label>
                  <input
                    type="text"
                    id="firstName"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="lastName">Achternaam</label>
                  <input
                    type="text"
                    id="lastName"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="phone">Telefoonnummer</label>
                  <input
                    type="tel"
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="Bijv. 06-12345678"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="department">Afdeling</label>
                  <input
                    type="text"
                    id="department"
                    value={profileData.department}
                    onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                    placeholder="Bijv. Communicatie"
                  />
                </div>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton} disabled={savingProfile}>
                  {savingProfile ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </div>
          </form>

          {/* Password Change */}
          <form onSubmit={handlePasswordSave}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Wachtwoord wijzigen</h2>
              <div className={styles.formGroup}>
                <label htmlFor="currentPassword">Huidig wachtwoord</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="newPassword">Nieuw wachtwoord</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                  <span className={styles.passwordHint}>Minimaal 6 tekens</span>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="confirmPassword">Bevestig wachtwoord</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton} disabled={savingPassword}>
                  {savingPassword ? 'Wijzigen...' : 'Wachtwoord wijzigen'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
